import { isPwaMode, isWeb, TrackingEventType, useAnalytics } from '@capsicum/common';
import { useCallback } from 'react';

const ONBOARDING_FEATURE_NAME = 'onboarding';

export const useOnboardingAnalytics = () => {
	const analytics = useAnalytics();

	//This is a private callback used by a handful of exported callbacks which share the code
	const sendCommonEvent = useCallback(
		({ eventName, page }: { eventName: string; page: string }) =>
			analytics.sendEvent(TrackingEventType.track, eventName, {
				feature: ONBOARDING_FEATURE_NAME,
				is_pwa_mode: isWeb() ? isPwaMode() : undefined,
				page
			}),
		[analytics]
	);

	const sendFirstAppOpenEvent = useCallback(
		() =>
			analytics.sendEvent(TrackingEventType.track, 'GA::first_app_open_onboarding', {
				feature: ONBOARDING_FEATURE_NAME,
				page: 'first_page'
			}),
		[analytics]
	);

	const sendWalkthroughFinishEvent = useCallback(
		() =>
			sendCommonEvent({
				eventName: `GA::walkthrough_onboarding_${isWeb() ? 'web' : 'mobile'}_view`,
				page: 'walkthrough_last_page'
			}),
		[sendCommonEvent]
	);

	const sendRegistrationPhoneEvent = useCallback(
		() =>
			sendCommonEvent({
				eventName: 'GA::register_phone_onboarding',
				page: 'registration_enter_phone'
			}),
		[sendCommonEvent]
	);

	const sendRegistrationCodeVerifyEvent = useCallback(
		(status: 'failure' | 'success') =>
			analytics.sendEvent(TrackingEventType.track, 'GA::code_verification_onboarding', {
				feature: ONBOARDING_FEATURE_NAME,
				is_pwa_mode: isWeb() ? isPwaMode() : undefined,
				page: 'code_verification',
				status
			}),
		[analytics]
	);

	const sendRegistrationEmailEvent = useCallback(
		() =>
			sendCommonEvent({
				eventName: 'GA::registration_email_onboarding',
				page: 'registration_enter_email'
			}),
		[sendCommonEvent]
	);

	const sendRegistrationCountryEvent = useCallback(
		() =>
			sendCommonEvent({
				eventName: 'GA::registration_country_onboarding',
				page: 'registration_enter_country'
			}),
		[sendCommonEvent]
	);

	const sendRegistrationPasskeyStartEvent = useCallback(
		() =>
			sendCommonEvent({
				eventName: 'GA::registration_passkey_onboarding_initiate',
				page: 'registration_initiate_passkey'
			}),
		[sendCommonEvent]
	);

	const sendRegistrationPasskeyCreationEvent = useCallback(
		(status: 'failure' | 'success') =>
			analytics.sendEvent(TrackingEventType.track, 'GA::registration_passkey_onboarding', {
				feature: ONBOARDING_FEATURE_NAME,
				is_pwa_mode: isWeb() ? isPwaMode() : undefined,
				page: 'registration_create_passkey',
				status
			}),
		[analytics]
	);

	const sendRegistrationRecoveryKitDownloadEvent = useCallback(
		() =>
			sendCommonEvent({
				eventName: 'GA::registration_recoverykit_onboarding_download',
				page: 'recoverykit_download'
			}),
		[sendCommonEvent]
	);

	const sendRegistrationRecoveryKitConfirmEvent = useCallback(
		() =>
			sendCommonEvent({
				eventName: 'GA::registration_recoverykit_onboarding_confirmation',
				page: 'recoverykit_warning'
			}),
		[sendCommonEvent]
	);

	const sendOnboardingSuccessEvent = useCallback(
		() =>
			sendCommonEvent({
				eventName: `GA::registration_onboarding_success_${isWeb() ? 'web' : 'mobile'}_view`,
				page: 'congratulations_page'
			}),
		[sendCommonEvent]
	);

	return {
		sendFirstAppOpenEvent,
		sendOnboardingSuccessEvent,
		sendRegistrationCodeVerifyEvent,
		sendRegistrationCountryEvent,
		sendRegistrationEmailEvent,
		sendRegistrationPasskeyCreationEvent,
		sendRegistrationPasskeyStartEvent,
		sendRegistrationPhoneEvent,
		sendRegistrationRecoveryKitConfirmEvent,
		sendRegistrationRecoveryKitDownloadEvent,
		sendWalkthroughFinishEvent
	};
};
